import axios from 'axios';

import {
  TemplateDTO,
  TemplateListDTO,
  CreateTemplateDTO,
  UpdateTemplateDTO,
  TemplateInstanceDTO,
  TemplateInstanceListDTO,
  AssignTemplateDTO,
  SubmitTemplateResponseDTO,
} from '../models/TemplateModels';
import { logApiError } from '../utils/errorUtils';

import { healioSpaceAPIURL } from './baseHealioSpaceAPI';
import { installSessionInterceptors } from './sessionInterceptors';

export const templatesApi = axios.create({
  baseURL: `${healioSpaceAPIURL}/api/v1`,
  withCredentials: true,
});

installSessionInterceptors(templatesApi);

// ============ Template CRUD ============

/**
 * Create a new template for a practice
 */
export const createTemplate = async (
  practiceId: string,
  request: CreateTemplateDTO
): Promise<TemplateDTO | null> => {
  try {
    const response = await templatesApi.post(
      `/practices/${encodeURIComponent(practiceId)}/templates`,
      request
    );

    return response.data;
  } catch (error) {
    console.error('Error creating template:', error);

    return null;
  }
};

/**
 * Get all templates for a practice.
 *
 * HA-350: This is the LIST fetch that drives the Template Library page. It
 * deliberately RE-THROWS on failure instead of returning an empty
 * `{ templates: [], totalCount: 0 }`. Swallowing the error made a 403/404/500
 * indistinguishable from a genuinely-empty practice, so the page always
 * showed "No templates found" (the original HA-350 symptom). The caller
 * surfaces a distinct error state on rejection. Pattern mirrors
 * subscriptionsAPI (logApiError + throw).
 */
export const getPracticeTemplates = async (
  practiceId: string,
  templateType?: string,
  activeOnly: boolean = true
): Promise<TemplateListDTO> => {
  try {
    const params = new URLSearchParams();

    if (templateType) params.append('templateType', templateType);
    if (!activeOnly) params.append('activeOnly', 'false');

    const response = await templatesApi.get(
      `/practices/${encodeURIComponent(practiceId)}/templates?${params}`
    );

    return response.data;
  } catch (error) {
    // Do NOT log the practiceId/email here — keep PHI/PII out of logs.
    logApiError('getPracticeTemplates', error);
    throw error;
  }
};

/**
 * Get a specific template
 */
export const getTemplate = async (
  templateId: string,
  practiceId: string
): Promise<TemplateDTO | null> => {
  try {
    const response = await templatesApi.get(
      `/templates/${templateId}?practiceId=${encodeURIComponent(practiceId)}`
    );

    return response.data;
  } catch (error) {
    console.error('Error getting template:', error);

    return null;
  }
};

/**
 * Update a template
 */
export const updateTemplate = async (
  templateId: string,
  practiceId: string,
  request: UpdateTemplateDTO
): Promise<TemplateDTO | null> => {
  try {
    const response = await templatesApi.put(
      `/templates/${templateId}?practiceId=${encodeURIComponent(practiceId)}`,
      request
    );

    return response.data;
  } catch (error) {
    console.error('Error updating template:', error);

    return null;
  }
};

/**
 * Delete (soft delete) a template
 */
export const deleteTemplate = async (templateId: string, practiceId: string): Promise<boolean> => {
  try {
    await templatesApi.delete(
      `/templates/${templateId}?practiceId=${encodeURIComponent(practiceId)}`
    );

    return true;
  } catch (error) {
    console.error('Error deleting template:', error);

    return false;
  }
};

/**
 * Get template count for tier limit checking
 */
export const getTemplateCount = async (practiceId: string): Promise<number> => {
  try {
    const response = await templatesApi.get(
      `/practices/${encodeURIComponent(practiceId)}/templates/count`
    );

    return response.data.count;
  } catch (error) {
    console.error('Error getting template count:', error);

    return 0;
  }
};

// ============ Template Instances ============

/**
 * Assign a template to a client
 */
export const assignTemplate = async (
  clientId: string,
  templateId: string,
  practiceId: string,
  request: AssignTemplateDTO
): Promise<TemplateInstanceDTO | null> => {
  try {
    const response = await templatesApi.post(
      `/clients/${clientId}/templates/${templateId}/assign?practiceId=${encodeURIComponent(practiceId)}`,
      request
    );

    return response.data;
  } catch (error) {
    console.error('Error assigning template:', error);

    return null;
  }
};

/**
 * Get all template instances for a client
 */
export const getClientInstances = async (clientId: string): Promise<TemplateInstanceListDTO> => {
  try {
    const response = await templatesApi.get(`/clients/${clientId}/assigned-templates`);

    return response.data;
  } catch (error) {
    console.error('Error getting client instances:', error);

    return { instances: [], totalCount: 0 };
  }
};

/**
 * Get a specific template instance
 */
export const getInstance = async (
  instanceId: string,
  clientId: string
): Promise<TemplateInstanceDTO | null> => {
  try {
    const response = await templatesApi.get(
      `/template-instances/${instanceId}?clientId=${clientId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error getting instance:', error);

    return null;
  }
};

/**
 * Submit responses for a template instance
 */
export const submitResponse = async (
  instanceId: string,
  clientId: string,
  request: SubmitTemplateResponseDTO
): Promise<TemplateInstanceDTO | null> => {
  try {
    const response = await templatesApi.post(
      `/template-instances/${instanceId}/submit?clientId=${clientId}`,
      request
    );

    return response.data;
  } catch (error) {
    console.error('Error submitting response:', error);

    return null;
  }
};

/**
 * Mark a template instance as reviewed
 */
export const reviewInstance = async (
  instanceId: string,
  clientId: string
): Promise<TemplateInstanceDTO | null> => {
  try {
    const response = await templatesApi.post(
      `/template-instances/${instanceId}/review?clientId=${clientId}`
    );

    return response.data;
  } catch (error) {
    console.error('Error reviewing instance:', error);

    return null;
  }
};

/**
 * Save draft responses for a template instance
 * NOTE: Currently uses localStorage. When backend endpoint is available,
 * update to call: POST /template-instances/{instanceId}/draft
 */
export const saveDraft = async (
  instanceId: string,
  _clientId: string,
  values: Record<string, unknown>
): Promise<boolean> => {
  try {
    // TODO: Replace with API call when backend endpoint is available
    // const response = await templatesApi.post(
    //   `/template-instances/${instanceId}/draft?clientId=${clientId}`,
    //   { responseJson: JSON.stringify(values) }
    // );
    // return !!response.data;

    // Temporary localStorage implementation
    const draftKey = `form_draft_${instanceId}`;

    localStorage.setItem(draftKey, JSON.stringify(values));

    return true;
  } catch (error) {
    console.error('Error saving draft:', error);

    return false;
  }
};

/**
 * Load draft responses for a template instance from localStorage
 */
export const loadDraft = (instanceId: string): Record<string, unknown> | null => {
  try {
    const draftKey = `form_draft_${instanceId}`;
    const draft = localStorage.getItem(draftKey);

    if (draft) {
      return JSON.parse(draft);
    }

    return null;
  } catch (error) {
    console.error('Error loading draft:', error);

    return null;
  }
};

/**
 * Clear draft responses for a template instance
 */
export const clearDraft = (instanceId: string): void => {
  const draftKey = `form_draft_${instanceId}`;

  localStorage.removeItem(draftKey);
};
